import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../utils/api";

const AddWorkOrder = ({
  open,
  handleClose,
  propertyId,
  onWorkOrderAdded,
  uploadedBy = 1,
  clubMembershipTypeCode = "",
  openedByUserId = null,
}) => {
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const [formData, setFormData] = useState({
    description: "",
    details: "",
    property_id: propertyId || "",
    workorder_type_id: "",
    workorder_subtype_id: "",
    location_id: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const submittingRef = useRef(false); // prevents duplicate submits
  const [requestId, setRequestId] = useState(uuidv4()); // unique submission ID

  // 🔹 Load Work Order Types
  useEffect(() => {
    let cancelled = false;
    const loadTypes = async () => {
      try {
        const url = propertyId
          ? `/workorder-types/by-property?property_id=${encodeURIComponent(
              propertyId
            )}`
          : `/workorder-types`;
        const res = await api.get(url);
        if (!cancelled) setWorkOrderTypes(res.data?.types || res.data || []);
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
        if (!cancelled) setWorkOrderTypes([]);
      }
    };
    loadTypes();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // 🔹 Load Subtypes & Locations when Type changes
  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchData = async () => {
      try {
        const [subRes, locRes] = await Promise.all([
          api.get(
            `/workorder-subtypes-by-type?type_id=${formData.workorder_type_id}`
          ),
          api.get(
            `/workorder-locations-by-type?type_id=${formData.workorder_type_id}`
          ),
        ]);
        setSubtypes(subRes.data);
        setLocations(locRes.data);
      } catch (err) {
        console.error("Failed to fetch subtypes/locations:", err);
        setSubtypes([]);
        setLocations([]);
      }
    };

    fetchData();
  }, [formData.workorder_type_id]);

  // 🔹 Handle Form Changes
  const handleFormChange = (e) => {
    const { name } = e.target;
    const rawValue = e.target.value;

    const value = name.endsWith("_id")
      ? rawValue === ""
        ? ""
        : Number(rawValue)
      : rawValue;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "workorder_type_id") {
        updated.workorder_subtype_id = "";
        updated.location_id = "";
        updated.description = "";
      }

      if (name === "workorder_subtype_id") {
        const selectedSubtype = subtypes.find(
          (s) => Number(s.id) === Number(value)
        );
        updated.description = selectedSubtype
          ? selectedSubtype.subtype_name
          : "";
      }

      return updated;
    });
  };

  // 🔹 Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) return; 
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      let {
        description,
        details,
        workorder_type_id,
        workorder_subtype_id,
        location_id,
      } = formData;

      const propertyIdEffective = (propertyId ?? formData.property_id ?? "")
        .toString()
        .trim();

      const toNum = (v) => (v === "" || v == null ? "" : Number(v));
      workorder_type_id = toNum(workorder_type_id);
      workorder_subtype_id = toNum(workorder_subtype_id);
      location_id = toNum(location_id);

      if ((!description || !description.trim()) && workorder_subtype_id) {
        const sel = subtypes.find(
          (s) => Number(s.id) === Number(workorder_subtype_id)
        );
        if (sel?.subtype_name) description = sel.subtype_name;
      }

      const descTrim = (description || "").trim();
      const detailsTrim = (details || "").trim();

      const missing = [];
      if (!descTrim) missing.push("description");
      if (!detailsTrim) missing.push("details");
      if (!propertyIdEffective) missing.push("property_id");
      if (!workorder_type_id) missing.push("workorder_type_id");
      if (!workorder_subtype_id) missing.push("workorder_subtype_id");
      if (!location_id) missing.push("location_id");

      if (missing.length) {
        setSnackbarMessage(
          `Please fill in all required fields: ${missing.join(", ")}`
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const payload = {
        description: descTrim,
        details: detailsTrim,
        property_id: propertyIdEffective,
        workorder_type_id,
        workorder_subtype_id,
        location_id,
        request_id: requestId,
        opened_by_user_id: Number.isFinite(Number(openedByUserId))
          ? Number(openedByUserId)
          : null,
      };

      const res = await api.post(`/addWorkOrder`, payload);
      const workorder_id = res.data?.workOrderId;
      if (!workorder_id) throw new Error("Missing workOrderId from response");

      if (selectedFiles.length > 0) {
  const fileData = new FormData();

  // Required fields
  fileData.append("workorder_id", workorder_id);
  fileData.append("uploaded_by", uploadedBy);

  // Attach all files under `upload_urls` (backend expects array)
  selectedFiles.forEach((file) => {
    fileData.append("upload_urls", file);
  });

  try {
    await api.post("/workorder-files", fileData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (err) {
    console.error("Error uploading work order files:", err);
    setSnackbarMessage(
      err.response?.data?.message || "File upload failed. Please try again."
    );
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  }
}


      setSnackbarMessage("✅ Work order created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setFormData({
        description: "",
        details: "",
        property_id: "",
        workorder_type_id: "",
        workorder_subtype_id: "",
        location_id: "",
      });
      setSelectedFiles([]);
      setRequestId(uuidv4());
      onWorkOrderAdded?.();
      setConfirmOpen(true);
    } catch (err) {
      console.error("Error creating work order:", err);
      setSnackbarMessage(
        err.response?.data?.message || "Failed to create work order."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // 🔹 File Upload (Dropzone)
 const onDrop = useCallback((acceptedFiles) => {
  setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
}, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*, .pdf, .doc, .docx",
    multiple: false,
  });

  // 🔹 JSX
  return (
    <>
      {/* Main Work Order Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Create New Work Order</DialogTitle>
          <DialogContent dividers>
            <input
              type="hidden"
              name="opened_by_user_id"
              value={openedByUserId ?? ""}
            />

            {/* Work Order Type */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="workorder-type-label">Work Order Type</InputLabel>
              <Select
                labelId="workorder-type-label"
                name="workorder_type_id"
                value={formData.workorder_type_id}
                onChange={handleFormChange}
                required
                label="Work Order Type"
              >
                {workOrderTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subtype */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="subtype-label">Subtype</InputLabel>
              <Select
                labelId="subtype-label"
                name="workorder_subtype_id"
                value={formData.workorder_subtype_id}
                onChange={handleFormChange}
                required
                disabled={!subtypes.length}
                label="Subtype"
              >
                {subtypes.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.subtype_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Location */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="location-label">Location</InputLabel>
              <Select
                labelId="location-label"
                name="location_id"
                value={formData.location_id}
                onChange={handleFormChange}
                required
                disabled={!locations.length}
                label="Location"
              >
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              disabled
              sx={{ mb: 2 }}
            />

            {/* Details */}
            <TextField
              fullWidth
              label="Details"
              name="details"
              value={formData.details}
              onChange={handleFormChange}
              required
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />

           {/* 🧲 Multi File Upload (Styled Dropzone) */}
<Box
  {...getRootProps()}
  sx={{
    border: "2px dashed #90caf9",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center",
    cursor: "pointer",
    mb: 2,
    backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
    transition: "background-color 0.2s",
  }}
>
  <input {...getInputProps()} />
  <Typography variant="body2" color="textSecondary">
    {isDragActive
      ? "Drop files here..."
      : "Drag & drop files here, or click to select"}
  </Typography>
</Box>

{/* 🗂 Selected File List */}
{selectedFiles.length > 0 && (
  <Box
    sx={{
      border: "1px solid #ddd",
      borderRadius: "8px",
      backgroundColor: "#fff",
      maxHeight: 150,
      overflowY: "auto",
      mb: 2,
    }}
  >
    {selectedFiles.map((file, index) => (
      <Box
        key={index}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
        sx={{
          borderBottom:
            index < selectedFiles.length - 1 ? "1px solid #eee" : "none",
        }}
      >
        <Typography variant="body2">{file.name}</Typography>
        <IconButton
          color="error"
          size="small"
          onClick={() =>
            setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
          }
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
  </Box>
)}

            {/* Property ID */}
            <TextField
              fullWidth
              label="Member ID"
              name="property_id"
              value={propertyId || ""}
              disabled
              sx={{ mb: 2 }}
            />
          </DialogContent>

          {/* Actions */}
          <DialogActions sx={{ px: 0 }}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            padding: 2,
            boxShadow: "0px 10px 25px rgba(0,0,0,0.15)",
            minWidth: 360,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "1.4rem",
            fontWeight: 600,
            color: "#111",
          }}
        >
          Work Order Created
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 2,
              p: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 500,
                color: "success.main",
              }}
            >
              ✅
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                color: "#444",
              }}
            >
              The work order has been successfully created.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleClose();
              setTimeout(() => {
                setIsReloading(true);
                setTimeout(() => {
                  window.location.reload();
                }, 1200);
              }, 100);
            }}
            variant="contained"
            sx={{
              borderRadius: "12px",
              paddingX: 4,
              paddingY: 1,
              fontSize: "0.95rem",
              textTransform: "none",
              background: "linear-gradient(180deg, #fff, #f0f0f0)",
              color: "#111",
              border: "1px solid #ccc",
              boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
              "&:hover": {
                background: "linear-gradient(180deg, #f9f9f9, #eaeaea)",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>

        {/* Fullscreen Loader */}
        {isReloading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress
              size={48}
              thickness={4}
              sx={{ color: "primary.main" }}
            />
            <Typography
              variant="h6"
              sx={{ color: "#333", fontWeight: 500 }}
            >
              Refreshing your dashboard…
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default AddWorkOrder;
